import LoadingSpinnerBox from '@/components/loading-spinner-box';
import castErrorToBaseError from '@/functions/castErrorToBaseError';
import useLoginUser from '@/modules/auth/hooks/useLoginUser';
import useAuthUserStore from '@/modules/auth/stores/useAuthUserStore';
import config from '@/modules/config/functions/config';
import {
  AuthRefreshUnauthorizedError,
  AuthStartupExpiredRefreshTokenError,
  AuthStartupInvalidAccessTokenError,
  AuthStartupInvalidRefreshTokenError,
  AuthStartupNoAccessTokenError,
  BaseError,
  CoreAuthUnauthorizedError,
} from '@/modules/errors/constants/error-class-map.gen';
import useQuery from '@/modules/query-client/hooks/useQuery';
import router from '@/modules/router/constants/router';
import { RouterProvider as TanStackRouterProvider } from '@tanstack/react-router';
import { useNotification } from '@thisisdmg/tirecloud-patterns/v2';
import { pick } from 'lodash-es';
import { AnimatePresence } from 'motion/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

/**
 * RouterProvider component
 * @returns RouterProvider component
 */
export default function RouterProvider() {
  const { t } = useTranslation();
  const userState = useAuthUserStore(
    useShallow((state) => pick(state, ['status', 'user']))
  );
  const loginUser = useLoginUser();
  const notification = useNotification();

  const { isLoading, error } = useQuery<
    ReturnType<typeof loginUser>,
    BaseError
  >('public', {
    queryKey: ['auth', 'user'],
    queryFn: async () => {
      return loginUser({ type: 'restore' });
    },
    retry(failureCount, error) {
      if (
        error instanceof AuthStartupNoAccessTokenError ||
        (config('features.auth.enableTokenRefreshing') !== true &&
          error instanceof AuthStartupInvalidAccessTokenError) ||
        error instanceof AuthStartupExpiredRefreshTokenError ||
        error instanceof AuthStartupInvalidRefreshTokenError ||
        error instanceof CoreAuthUnauthorizedError ||
        error instanceof AuthRefreshUnauthorizedError
      )
        return false;
      return failureCount < config('misc.defaultMutationRetryCount', 3);
    },
    staleTime: (resp) => {
      if (resp.state.error) return 0;
      return Infinity;
    },
  });

  useEffect(() => {
    if (!error) return;
    if (error instanceof AuthStartupNoAccessTokenError) return;
    const e = castErrorToBaseError(error);

    notification.error({
      key: e.id,
      message: `0x${e.id.toString(16).toUpperCase()}`,
      description: t(`errors.gen.${e.path}`, { errorId: e.id }),
    });
  }, [error, notification, t]);

  return (
    <AnimatePresence>
      {isLoading ? <LoadingSpinnerBox key="auth_loading_spinner_box" /> : null}
      {!isLoading && (
        <TanStackRouterProvider
          router={router}
          context={{
            userState,
            authStartupError: castErrorToBaseError(error),
          }}
        />
      )}
    </AnimatePresence>
  );
}
